import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import { cafeteriaClient } from "../apiClient/cafeteriaClient";
import { ApiError } from "../../platform/apiClient/apiRequest";
import { useAuth } from "../../gym/auth/useAuth";
import { formatEgp } from "../utils/money";
import type { CafeteriaCouponStatus, CafeteriaMenuItem, CafeteriaTransaction, CafeteriaWallet } from "../types/cafeteria.types";

function TokenQr({ token, label, width = 220 }: { token: string; label: string; width?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (canvasRef.current) {
      void QRCode.toCanvas(canvasRef.current, token, { width, margin: 1 });
    }
  }, [token, width]);

  return <canvas ref={canvasRef} className="wallet-qr" aria-label={label} />;
}

function formatTransactionDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function transactionDescription(tx: CafeteriaTransaction): string {
  if (tx.type === "charge") {
    const items = tx.items?.map((i) => `${i.quantity}× ${i.name}`).join(", ") ?? "Purchase";
    return tx.note ? `${items} (${tx.note})` : items;
  }
  if (tx.type === "coupon") {
    const item = tx.items?.[0]?.name ?? "item";
    return `🎟️ Free meal — ${item}`;
  }
  return tx.note ? `Top-up — ${tx.note}` : "Top-up";
}

function PurchaseHistory() {
  const [transactions, setTransactions] = useState<CafeteriaTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void cafeteriaClient
      .getWalletTransactions()
      .then(setTransactions)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load your history."))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="wallet-history">
      <h2 className="admin-section-heading">Purchase history</h2>
      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading ? (
        <div className="state-panel">Loading…</div>
      ) : transactions.length === 0 ? (
        <div className="state-panel">
          <p>No purchases or top-ups yet.</p>
        </div>
      ) : (
        <ul className="wallet-history-list">
          {transactions.map((tx) => (
            <li key={tx.id} className="wallet-history-row">
              <div className="wallet-history-row-main">
                <span className="wallet-history-desc">{transactionDescription(tx)}</span>
                <span className="wallet-history-date">{formatTransactionDate(tx.createdAt)}</span>
              </div>
              <div className="wallet-history-row-amounts">
                <span className={tx.amount >= 0 ? "cafeteria-amount--credit" : "cafeteria-amount--debit"}>
                  {tx.amount >= 0 ? "+" : ""}
                  {formatEgp(tx.amount)}
                </span>
                <span className="wallet-history-balance">Balance after: {formatEgp(tx.balanceAfter)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MenuBrowser() {
  const [menu, setMenu] = useState<CafeteriaMenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  // Collapsed by default so the page opens short — searching/filtering bypasses this and
  // just shows what matched.
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    void cafeteriaClient
      .getMenu()
      .then(setMenu)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load the menu."))
      .finally(() => setIsLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(menu.map((i) => i.category).filter(Boolean))].sort(), [menu]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return menu.filter((item) => {
      if (categoryFilter && item.category !== categoryFilter) return false;
      if (!q) return true;
      return item.name.toLowerCase().includes(q) || item.category.toLowerCase().includes(q);
    });
  }, [menu, query, categoryFilter]);

  // The menu already comes back sorted category-then-name, so consecutive runs of the
  // same category can just be grouped in a single pass.
  const groups = useMemo(() => {
    const out: { category: string; items: CafeteriaMenuItem[] }[] = [];
    for (const item of filtered) {
      const last = out[out.length - 1];
      if (last && last.category === item.category) last.items.push(item);
      else out.push({ category: item.category, items: [item] });
    }
    return out;
  }, [filtered]);

  const isSearchActive = query.trim() !== "" || categoryFilter !== "";

  function toggleCategory(category: string) {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }

  if (error || isLoading || menu.length === 0) {
    return (
      <div className="wallet-menu">
        <h2 className="admin-section-heading">Menu</h2>
        {error ? (
          <div className="state-panel">
            <p>{error}</p>
          </div>
        ) : isLoading ? (
          <div className="state-panel">Loading…</div>
        ) : (
          <div className="state-panel">
            <p>No menu items yet.</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="wallet-menu">
      <h2 className="admin-section-heading">Menu</h2>
      <div className="cafeteria-menu-filters">
        <input
          type="text"
          className="coach-search"
          placeholder="Search by name or category…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search menu"
        />
        <select className="coach-search" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Filter by category">
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
      {groups.length === 0 ? (
        <div className="state-panel">
          <p>No items match.</p>
        </div>
      ) : (
        <div className="cafeteria-menu-groups">
          {groups.map((group) => {
            const isOpen = isSearchActive || expandedCategories.has(group.category);
            return (
              <div key={group.category} className="cafeteria-menu-category">
                <button
                  type="button"
                  className="cafeteria-menu-category-toggle"
                  onClick={() => toggleCategory(group.category)}
                  aria-expanded={isOpen}
                >
                  <span className="cafeteria-menu-category-toggle-label">
                    <span className="cafeteria-menu-category-name">{group.category || "Uncategorized"}</span>
                    <span className="cafeteria-menu-category-count">({group.items.length})</span>
                  </span>
                  <span className={isOpen ? "cafeteria-menu-category-chevron is-open" : "cafeteria-menu-category-chevron"}>▾</span>
                </button>
                {isOpen && (
                  <ul className="cafeteria-menu-item-list">
                    {group.items.map((item) => (
                      <li key={item.id} className="cafeteria-menu-item-row">
                        <span className="cafeteria-menu-item-name">{item.name}</span>
                        <span className="cafeteria-menu-item-price">{formatEgp(item.price)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CouponBanner() {
  const [status, setStatus] = useState<CafeteriaCouponStatus | null>(null);

  useEffect(() => {
    void cafeteriaClient.getMyCoupon().then(setStatus).catch(() => setStatus({ available: false }));
  }, []);

  if (!status?.available) return null;

  return (
    <div className="wallet-coupon-banner">
      <p className="wallet-coupon-banner-text">
        🎟️ You have a free meal waiting — {status.description}
        {status.expiresAt && <> (expires {status.expiresAt})</>}. Show this code at the counter.
      </p>
      {status.qrToken && <TokenQr token={status.qrToken} label="Your free meal coupon QR code" width={180} />}
    </div>
  );
}

// Cafeteria isn't fully live yet — hide the wallet QR card and purchase history for now.
// Flip these back to true once it's ready; the menu/coupon banner stay visible in the
// meantime since browsing is fine even before payment is turned on.
const SHOW_WALLET_CARD = false;
const SHOW_PURCHASE_HISTORY = false;
const SHOW_MENU = true;

export function CafeteriaPage() {
  const { isAdmin, isCafeteria } = useAuth();
  const [wallet, setWallet] = useState<CafeteriaWallet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  useEffect(() => {
    void cafeteriaClient
      .getWallet()
      .then(setWallet)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load your wallet."))
      .finally(() => setIsLoading(false));
  }, []);

  async function handleRegenerate() {
    setRegenerating(true);
    setRegenerateError(null);
    try {
      const updated = await cafeteriaClient.regenerateToken();
      setWallet(updated);
      setConfirmingRegenerate(false);
    } catch (err) {
      setRegenerateError(err instanceof ApiError ? err.message : "Couldn't regenerate your code.");
    } finally {
      setRegenerating(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Cafeteria</h1>
          <p className="page-sub">Browse the menu below.</p>
        </div>
        <div className="page-header-actions">
          {isCafeteria && (
            <Link to="/cafeteria/counter" className="btn btn-ghost">
              Open counter
            </Link>
          )}
          {isAdmin && (
            <Link to="/cafeteria/admin" className="btn btn-ghost">
              Manage cafeteria
            </Link>
          )}
        </div>
      </header>

      {error ? (
        <div className="state-panel">
          <p>{error}</p>
        </div>
      ) : isLoading || !wallet ? (
        <div className="state-panel">Loading…</div>
      ) : SHOW_WALLET_CARD ? (
        <div className="wallet-card">
          <span className="wallet-balance-label">Balance</span>
          <span className="wallet-balance">{formatEgp(wallet.balance)}</span>

          <TokenQr token={wallet.qrToken} label="Your cafeteria wallet QR code" />

          {confirmingRegenerate ? (
            <div className="cancel-confirm">
              <span>Your current code will stop working immediately.</span>
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmingRegenerate(false)} disabled={regenerating}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={handleRegenerate} disabled={regenerating}>
                {regenerating ? "Regenerating…" : "Regenerate"}
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmingRegenerate(true)}>
              Regenerate my code
            </button>
          )}
          {regenerateError && <div className="form-banner form-banner--error">{regenerateError}</div>}
        </div>
      ) : null}

      {!error && !isLoading && wallet && <CouponBanner />}
      {!error && !isLoading && wallet && SHOW_PURCHASE_HISTORY && <PurchaseHistory />}
      {!error && !isLoading && wallet && SHOW_MENU && <MenuBrowser />}
    </div>
  );
}
